import merge from 'lodash.merge'
import Type from 'webmango-types'
import {unique as shorthash} from 'shorthash'
import deepEqual from 'deep-eql'
import uniqid from 'uniqid'
import Path from './path'
import Errors from './errors'
import mixin from './mixin'

export { handleErrorResponse, fetch, fetchJSON } from './fetch'

function nameToID (name) {
  return name.trim().toLowerCase().replace(/\s+/gi, '-').replace(/[^a-z0-9-.~_]/gi, '')
}

function throttlePromise (fn, timeout = 30000) {
  let lastTime = Infinity
  let lastPromise = null
  let isPending = false
  return function (...args) {
    const now = Date.now()
    if (
      isPending ||
      (lastPromise && (now - lastTime) < timeout)
    ) return lastPromise

    isPending = true
    lastPromise = fn.call(this, ...args)
    return lastPromise.finally(() => {
      lastTime = now
      isPending = false
    })
  }
}

function getEmailID (email, ...values) {
  let prefix = email
  if (email.endsWith('@gmail.com')) {
    prefix = email.split('@')[0]
  }
  const tail = values.join('~').toLowerCase()
  const slug = sanitizeID(prefix)
  const hash = shorthash(`${email}${tail}`)
  return `${slug}-${tail}-${hash}`
}

/**
 * Generate an ID from a prefix + value.
 * The value will be hashed.
 */

function getID (prefix, ...values) {
  Type.assert(prefix, Type.NonEmptyString, 'prefix')
  Type.assert(values[0], Type.NonEmptyString, 'value')
  const key = [prefix, ...values].join('~').toLowerCase()
  const slug = sanitizeID(key)
  const hash = shorthash(key)
  return `${slug}-${hash}`
}

function sanitizeID (value) {
  return encodeURIComponent(value.trim().toLowerCase().replace(/\s+/gi, '-').replace(/[^a-z0-9-.~_]/gi, '~'))
}

const masks = new WeakMap()
const MASK = { _id: undefined, _rev: undefined, _revisions: undefined }

function isEqual (a, b, options = {}) {
  if (a === b) return true
  let maskedA
  let maskedB
  if (masks.has(a)) {
    maskedA = masks.get(a)
  } else {
    maskedA = Object.assign(Object.create(a), MASK)
    masks.set(a, maskedA)
  }

  if (masks.has(b)) {
    maskedB = masks.get(b)
  } else {
    maskedB = Object.assign(Object.create(b), MASK)
    masks.set(b, maskedB)
  }

  return deepEqual(maskedA, maskedB, options)
}

/**
 * Promise locking util
 */

class PromiseMutex {
  constructor () {
    this._lock = Promise.resolve()
  }
  /**
   * All Promise APIs should wait for ready
   */

  ready () {
    this._lock = this._lock.then(Promise.resolve())
    return this._lock
  }

  /**
   * Unlocks API. Triggers any queued Promises.
   */

  unlock () {
    const then = this.ready()
    this._lock = Promise.resolve()
    this._resolveLock()
    return Promise.resolve(then)
  }

  /**
   * Locks API. Any ready().then promises will be queued until unlock().
   */

  lock () {
    // wait for any existing lock
    const prev = Promise.resolve(this._lock)
    this._lock = prev.then(() => {
      return new Promise(resolve => {
        // set lock to wait for resolve
        this._resolveLock = resolve
      })
    })
    return prev
  }

  thenUnlock () {
    return [
      value => {
        this.unlock()
        return value
      }, err => {
        this.unlock()
        throw err
      }
    ]
  }

  static wrap (target, mutex, names) {
    const noMutex = Object.create(target)
    names.forEach(name => {
      const original = target[name]
      if (typeof original !== 'function') return // ignore non-functions
      noMutex[name] = original.bind(noMutex) // provide access to non-mutex version

      target[name] = async (...args) => {
        await mutex.ready()
        return original.call(target, ...args)
      }
    })
    return noMutex
  }

  static methodsOf (target) {
    return Object.getOwnPropertyNames(Object.getPrototypeOf(target)).filter(name => {
      return typeof target[name] === 'function' // ignore non-functions
    })
  }
}

/**
 * only permit one promise at a time per key
 */

function one (fn) {
  let inProgress = new Map()
  const wrapper = (key, ...args) => {
    const existingTask = inProgress.get(key)
    if (existingTask) return existingTask
    const task = fn(key, ...args).finally(() => {
      wrapper.inProgress--
      inProgress.delete(key)
    })

    wrapper.inProgress++
    inProgress.set(key, task)
    return task
  }

  wrapper.checkProgress = (key) => {
    return inProgress.has(key)
  }

  wrapper.inProgress = 0

  return wrapper
}

const hasOwnProperty = Object.prototype.hasOwnProperty

/**
 * inlined Object.is polyfill to avoid requiring consumers ship their own
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/is
 */

function is (x, y) {
  // SameValue algorithm
  if (x === y) {
    // Steps 1-5, 7-10
    // Steps 6.b-6.e: +0 != -0
    // Added the nonzero y check to make Flow happy, but it is redundant
    return x !== 0 || y !== 0 || 1 / x === 1 / y
  } else {
    // Step 6.a: NaN == NaN
    // eslint-disable-next-line
    return x !== x && y !== y
  }
}

/**
 * Performs equality by iterating through keys on an object and returning false
 * when any key has values which are not strictly equal between the arguments.
 * Returns true when the values of all keys are strictly equal.
 */

function shallowEqual (objA, objB) {
  if (is(objA, objB)) {
    return true
  }

  if (typeof objA !== 'object' || objA === null || typeof objB !== 'object' || objB === null) {
    return false
  }

  const keysA = Object.keys(objA)
  const keysB = Object.keys(objB)

  if (keysA.length !== keysB.length) {
    return false
  }

  // Test for A's keys different from B.
  for (let i = 0; i < keysA.length; i++) {
    if (!hasOwnProperty.call(objB, keysA[i]) || !is(objA[keysA[i]], objB[keysA[i]])) {
      return false
    }
  }

  return true
}

export function nowISOString () {
  return new Date(new Date().toGMTString()).toISOString()
}

// Exports

export {
  nameToID,
  getEmailID,
  shorthash,
  isEqual,
  shallowEqual,
  Errors,
  merge,
  throttlePromise,
  getID,
  sanitizeID,
  uniqid,
  PromiseMutex,
  Path,
  mixin,
  one
}



// WEBPACK FOOTER //
// ../webmango-utils/index.js